/**
 * External extension debug toggle helpers for /help/contact.
 *
 * WHAT THIS FILE DOES:
 * - Sends externally_connectable messages to the Gist extension.
 * - Reads and updates the extension logging mode (off/debug/trace).
 * - Normalizes errors so the UI can show clear states.
 *
 * WHY THIS EXISTS:
 * - /help/contact runs on the web, not inside the extension.
 * - We need a small bridge to talk to the extension without content scripts.
 * - Keeping this logic isolated avoids coupling UI code to Chrome APIs.
 *
 * ARCHITECTURE CONTEXT:
 * - Extension manifest covers gist.is/* paths as externally_connectable; the
 *   support form at /help/contact is the only page that uses the debug toggle.
 * - background/core/router.js handles EXTERNAL_DEBUG_MODE_GET/SET.
 * - This module only runs in Chromium-based browsers.
 *
 * RELATED FILES:
 * - packages/chrome-plugin/extension/manifest.json
 * - packages/chrome-plugin/extension/background/core/router.js
 * - packages/chrome-plugin/extension/shared/message-types.js
 *
 * NON-GOALS:
 * - No retries/backoff (keep UX simple).
 * - No auto-install flow (we only link to the Web Store).
 */

export type DebugMode = 'off' | 'debug' | 'trace';

export type DebugStatus =
  | { state: 'available'; loggingMode: DebugMode; extensionVersion: string; extensionId: string }
  | { state: 'missing' }
  | { state: 'error'; error: string };

const ExternalMessageTypes = {
  GET: 'EXTERNAL_DEBUG_MODE_GET',
  SET: 'EXTERNAL_DEBUG_MODE_SET'
} as const;

const ExtensionIds = [
  // Stable production extension ID (Chrome Web Store / Brave Web Store listing).
  'iilleobakpmdhineleplnjifbpoiioga',
  // Legacy ID retained for older dev builds and archived installs.
  'madipiionhmndebpacebdnnbomeicbim'
] as const;

/**
 * Fetch the current debug mode from the installed extension.
 */
export async function fetchDebugMode(): Promise<DebugStatus> {
  const runtime = getRuntime();
  if (!runtime) {
    return { state: 'missing' };
  }

  for (const extensionId of ExtensionIds) {
    const response = await sendExternalMessage(runtime, extensionId, { type: ExternalMessageTypes.GET });
    if (response.state === 'missing') {
      continue;
    }
    if (response.state === 'error') {
      return response;
    }
    return {
      state: 'available',
      loggingMode: response.loggingMode,
      extensionVersion: response.extensionVersion,
      extensionId
    };
  }

  return { state: 'missing' };
}

/**
 * Update the debug mode in the installed extension.
 *
 * @param mode - Logging mode to set.
 */
export async function setDebugMode(mode: DebugMode): Promise<DebugStatus> {
  const runtime = getRuntime();
  if (!runtime) {
    return { state: 'missing' };
  }

  for (const extensionId of ExtensionIds) {
    const response = await sendExternalMessage(runtime, extensionId, {
      type: ExternalMessageTypes.SET,
      loggingMode: mode
    });
    if (response.state === 'missing') {
      continue;
    }
    if (response.state === 'error') {
      return response;
    }
    return {
      state: 'available',
      loggingMode: response.loggingMode,
      extensionVersion: response.extensionVersion,
      extensionId
    };
  }

  return { state: 'missing' };
}

/**
 * Detects and returns the Chrome extension runtime object when available.
 *
 * Returns null in non-Chromium browsers or environments where
 * chrome.runtime.sendMessage is not a function.
 *
 * @returns Chrome runtime object with sendMessage, or null when unavailable.
 */
function getRuntime(): { sendMessage: Function; lastError?: { message?: string } } | null {
  const chromeRuntime = (globalThis as any)?.chrome?.runtime;
  if (!chromeRuntime || typeof chromeRuntime.sendMessage !== 'function') {
    return null;
  }
  return chromeRuntime;
}

/**
 * Sends an externally_connectable message to a Chrome extension and normalizes the response.
 *
 * Wraps chrome.runtime.sendMessage in a Promise. Distinguishes between
 * "extension not installed" (state: missing), runtime errors (state: error),
 * and successful responses (state: ok) with logging mode and version.
 *
 * @param runtime - Chrome runtime object from getRuntime().
 * @param extensionId - Target extension ID to message.
 * @param message - Payload to send (e.g. GET/SET debug mode).
 * @returns Normalized response indicating ok, missing, or error state.
 */
async function sendExternalMessage(
  runtime: { sendMessage: Function; lastError?: { message?: string } },
  extensionId: string,
  message: Record<string, unknown>
): Promise<{ state: 'ok'; loggingMode: DebugMode; extensionVersion: string } | { state: 'missing' } | { state: 'error'; error: string } > {
  return new Promise((resolve) => {
    try {
      runtime.sendMessage(extensionId, message, (response: any) => {
        const lastError = runtime.lastError;
        if (lastError?.message) {
          const lowered = lastError.message.toLowerCase();
          if (lowered.includes('receiving end does not exist') || lowered.includes('could not establish connection')) {
            resolve({ state: 'missing' });
            return;
          }
          resolve({ state: 'error', error: lastError.message });
          return;
        }

        if (!response || response.ok !== true) {
          const errorMessage = typeof response?.error === 'string' ? response.error : 'Extension did not respond.';
          resolve({ state: 'error', error: errorMessage });
          return;
        }

        const loggingMode = response.loggingMode as DebugMode;
        const extensionVersion = typeof response.extensionVersion === 'string' ? response.extensionVersion : 'unknown';
        resolve({ state: 'ok', loggingMode, extensionVersion });
      });
    } catch (error) {
      resolve({ state: 'error', error: error instanceof Error ? error.message : String(error) });
    }
  });
}
